import React, { useState, useEffect } from 'react'

interface LiveEntry {
  position: number; driverNumber: number; driverName: string
  speed: number; location: string; isDNF: boolean
}
interface Props {
  assignments: { playerName: string; drivers: { number: number; name: string }[] }[]
  prizes: { first: number; second: number; third: number }
  finalLeaderboard: LiveEntry[]
  onNewGame: () => void
}

interface PlayerResult {
  playerName: string
  payout: number
  bestPosition: number | null
  drivers: { name: string; number: number; position: number; isDNF: boolean }[]
}

export default function Results({ assignments, prizes, finalLeaderboard, onNewGame }: Props) {
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const results: PlayerResult[] = assignments.map(a => {
    const drivers = a.drivers
      .map(d => {
        const entry = finalLeaderboard.find(e => e.driverNumber === d.number)
        return {
          name: d.name,
          number: d.number,
          position: entry?.position ?? 999,
          isDNF: entry?.isDNF ?? false
        }
      })
      .sort((x, y) => x.position - y.position)

    let payout = 0
    drivers.forEach(d => {
      if (d.position === 1) payout += prizes.first
      if (d.position === 2) payout += prizes.second
      if (d.position === 3) payout += prizes.third
    })

    return {
      playerName: a.playerName,
      payout,
      bestPosition: drivers[0]?.position !== 999 ? drivers[0].position : null,
      drivers
    }
  }).sort((a, b) => b.payout - a.payout || (a.bestPosition ?? 999) - (b.bestPosition ?? 999))

  useEffect(() => {
    // Auto-save results
    setSaving(true)
    fetch('/api/results/finish', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ finalLeaderboard })
    })
      .then(() => setSaved(true))
      .catch(console.error)
      .finally(() => setSaving(false))
  }, [])

  const top3 = results.slice(0, 3)
  const rest = results.slice(3)

  return (
    <div>
      <div className="race-header">
        <div className="section-title" style={{ margin: 0 }}>
          <span className="flag-anim">🏁</span> Race Results
        </div>
        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
          {saved && <span style={{ fontSize: '0.8rem', color: '#86efac' }}>✅ Results saved</span>}
          <button className="btn btn-primary" onClick={onNewGame}>🔄 New Game</button>
        </div>
      </div>

      {/* Podium */}
      <div className="results-podium">
        {[top3[1], top3[0], top3[2]].filter(Boolean).map((p, i) => {
          const actualRank = [1, 0, 2][i]
          const cls = ['second', 'first', 'third'][i]
          const trophies = ['🥈', '🏆', '🥉']
          return (
            <div className={`podium-place ${cls}`} key={p.playerName}>
              <div className="podium-trophy">{trophies[i]}</div>
              <div className="podium-player">{p.playerName}</div>
              <div className="podium-payout">${p.payout}</div>
              {p.bestPosition && (
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.3rem' }}>
                  Best: P{p.bestPosition}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Full table */}
      <div className="card">
        <table className="results-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Player</th>
              <th>Best Finish</th>
              <th>Drivers</th>
              <th>Payout</th>
            </tr>
          </thead>
          <tbody>
            {results.map((p, i) => (
              <tr key={p.playerName}>
                <td style={{ fontWeight: 700 }}>#{i + 1}</td>
                <td style={{ fontWeight: 600 }}>{p.playerName}</td>
                <td>
                  {p.bestPosition
                    ? <span style={{ color: p.bestPosition <= 3 ? '#d4a017' : 'inherit' }}>P{p.bestPosition}</span>
                    : '—'
                  }
                </td>
                <td>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {p.drivers.slice(0, 3).map(d => (
                      <span key={d.number} style={{
                        background: 'rgba(255,255,255,0.08)', borderRadius: '4px',
                        padding: '0.15rem 0.4rem', fontSize: '0.72rem'
                      }}>
                        #{d.number} P{d.position === 999 ? '?' : d.position}
                        {d.isDNF ? ' DNF' : ''}
                      </span>
                    ))}
                    {p.drivers.length > 3 && (
                      <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                        +{p.drivers.length - 3}
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ fontWeight: 700, color: p.payout > 0 ? '#d4a017' : 'inherit' }}>
                  ${p.payout}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
